import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import { getPendingUploads, processQueue, clearCompleted } from '../../services/offlineUploadQueue';

const STATUS_CONFIG = {
  pending: { icon: <HourglassEmptyIcon fontSize="small" />, color: 'warning', label: 'Pending' },
  uploading: { icon: <SyncIcon fontSize="small" />, color: 'info', label: 'Uploading' },
  completed: { icon: <CheckCircleIcon fontSize="small" />, color: 'success', label: 'Done' },
  failed: { icon: <ErrorIcon fontSize="small" />, color: 'error', label: 'Failed' },
};

/**
 * Shows a cloud icon with badge count of pending offline uploads.
 * Click to see queue details and manually trigger sync.
 */
export default function OfflineUploadStatus() {
  const [uploads, setUploads] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const pending = await getPendingUploads();
      setUploads(pending);
    } catch {
      // IndexedDB not available
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 5000);

    // Listen for SW background sync message
    const handleMessage = (event) => {
      if (event.data?.type === 'PROCESS_UPLOAD_QUEUE') {
        handleSync();
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      clearInterval(interval);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [refresh]);

  const handleSync = async () => {
    setSyncing(true);
    await processQueue((id, status) => {
      setUploads((prev) =>
        prev.map((u) => (u.id === id ? { ...u, status } : u))
      );
    });
    await refresh();
    setSyncing(false);
  };

  const handleClear = async () => {
    await clearCompleted();
    await refresh();
  };

  const pendingCount = uploads.filter((u) => u.status !== 'completed').length;

  if (uploads.length === 0) return null;

  return (
    <>
      <IconButton
        color="inherit"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={`${pendingCount} pending uploads`}
      >
        <Badge badgeContent={pendingCount} color="warning" max={99}>
          <CloudUploadIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 320, maxHeight: 400, overflow: 'auto' }}>
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">Offline Upload Queue</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {navigator.onLine && pendingCount > 0 && (
                <Button size="small" onClick={handleSync} disabled={syncing}>
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </Button>
              )}
              <Button size="small" color="secondary" onClick={handleClear}>
                Clear Done
              </Button>
            </Box>
          </Box>
          <Divider />
          <List dense>
            {uploads.map((upload) => {
              const config = STATUS_CONFIG[upload.status] || STATUS_CONFIG.pending;
              return (
                <ListItem key={upload.id}>
                  <ListItemIcon sx={{ minWidth: 32 }}>{config.icon}</ListItemIcon>
                  <ListItemText
                    primary={upload.fileName}
                    secondary={
                      upload.error
                        ? `${config.label} — ${upload.error}`
                        : `${config.label} — ${(upload.fileSize / 1024).toFixed(0)} KB`
                    }
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Chip label={config.label} size="small" color={config.color} variant="outlined" />
                </ListItem>
              );
            })}
          </List>
          {!navigator.onLine && (
            <Box sx={{ p: 1.5, bgcolor: 'warning.50' }}>
              <Typography variant="caption" color="warning.main">
                You're offline. Files will sync automatically when reconnected.
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}
